<template>
  <v-dialog
    v-model="isOpen"
    :fullscreen="$vuetify.breakpoint.smAndDown"
    max-width="560"
    scrollable
    persistent>
    <v-card>
      <v-toolbar flat dense color="primary" dark>
        <v-toolbar-title>{{
          isEdit
            ? $t('sharedAccounts.edit.title')
            : $t('sharedAccounts.add.title')
        }}</v-toolbar-title>
        <v-spacer />
        <v-btn icon :disabled="submitting" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pt-4">
        <v-alert v-if="serverError" type="error" text dense>
          {{ serverError }}
        </v-alert>

        <v-alert
          v-if="isEdit && account.borrowed_by_session_id"
          type="warning"
          text
          dense>
          {{ $t('sharedAccounts.edit.borrowedWarning') }}
        </v-alert>

        <v-form ref="form" @submit.prevent="submit">
          <v-select
            v-model="form.app_id"
            :items="launchers"
            :label="$t('sharedAccounts.add.launcher')"
            :rules="[required]"
            :disabled="submitting || isEdit"
            :hint="isEdit ? $t('sharedAccounts.edit.launcherLocked') : ''"
            :persistent-hint="isEdit"
            outlined
            dense
            hide-details="auto"
            class="mb-3" />

          <v-text-field
            v-model="form.username"
            :label="$t('sharedAccounts.add.username')"
            :rules="[required]"
            :disabled="submitting"
            outlined
            dense
            hide-details="auto"
            class="mb-3" />

          <v-text-field
            v-model="form.password"
            :label="$t('sharedAccounts.add.password')"
            :rules="passwordRules"
            :disabled="submitting"
            :type="showPassword ? 'text' : 'password'"
            :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
            :hint="isEdit ? $t('sharedAccounts.edit.leaveBlank') : ''"
            :persistent-hint="isEdit"
            outlined
            dense
            hide-details="auto"
            class="mb-3"
            @click:append="showPassword = !showPassword" />

          <v-text-field
            v-model="form.secret"
            :label="$t('sharedAccounts.add.secret')"
            :disabled="submitting"
            :type="showSecret ? 'text' : 'password'"
            :append-icon="showSecret ? 'mdi-eye-off' : 'mdi-eye'"
            :hint="isEdit ? $t('sharedAccounts.edit.leaveBlank') : ''"
            :persistent-hint="isEdit"
            outlined
            dense
            hide-details="auto"
            class="mb-3"
            @click:append="showSecret = !showSecret" />

          <v-text-field
            v-model="form.email"
            :label="$t('sharedAccounts.add.email')"
            :disabled="submitting"
            outlined
            dense
            hide-details="auto"
            class="mb-3" />

          <v-autocomplete
            v-model="form.game_ids"
            :items="gameItems"
            :search-input.sync="gameSearch"
            :loading="gamesLoading"
            :label="$t('sharedAccounts.add.games')"
            :rules="[gamesRule]"
            :disabled="submitting"
            :no-data-text="gamesNoData"
            prepend-inner-icon="mdi-magnify"
            multiple
            chips
            small-chips
            deletable-chips
            outlined
            dense
            hide-details="auto"
            class="mb-3">
            <template v-slot:append-item>
              <div
                v-if="gamesHasMore"
                v-intersect="onGamesScrollEnd"
                class="text-center py-2">
                <v-progress-circular
                  v-if="gamesLoadingMore"
                  indeterminate
                  size="16"
                  width="2"
                  color="primary" />
              </div>
            </template>
          </v-autocomplete>

          <v-autocomplete
            v-model="form.arena_ids"
            :items="visibleArenaItems"
            :search-input.sync="arenaSearch"
            :label="$t('sharedAccounts.add.arenas')"
            :rules="[arenasRule]"
            :disabled="submitting || arenasLoading"
            :loading="arenasLoading"
            :no-data-text="$t('components.noData.noEntriesFound')"
            :hint="arenasHint"
            :persistent-hint="!!arenasHint"
            no-filter
            multiple
            chips
            small-chips
            deletable-chips
            outlined
            dense
            hide-details="auto"
            class="mb-3" />

          <v-autocomplete
            v-model="form.linked_account_id"
            :items="linkedItems"
            :search-input.sync="linkedSearch"
            :loading="linkedLoading"
            :label="$t('sharedAccounts.add.linkedAccount')"
            :disabled="submitting"
            :no-data-text="$t('components.noData.noEntriesFound')"
            clearable
            outlined
            dense
            hide-details="auto" />
        </v-form>
      </v-card-text>

      <v-divider />
      <v-card-actions class="px-4 py-3">
        <v-spacer />
        <v-btn text :disabled="submitting" @click="close">
          {{ $t('miscellaneous.cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          :loading="submitting"
          :disabled="submitting"
          @click="submit">
          {{
            isEdit
              ? $t('sharedAccounts.edit.submit')
              : $t('sharedAccounts.add.submit')
          }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ArenasApi from '@/services/setup/ArenasApi'
import GamesApi from '@/services/configuration/GamesApi'
import SharedAccount from '@/models/configuration/SharedAccount'
import SharedAccountsApi from '@/services/configuration/SharedAccountsApi'
import required from '@/mixins/rules/required'
import { mapActions } from 'vuex'

// Some games ship on several launchers, so the account being created often has
// a counterpart on a different launcher worth linking. When such a game is
// picked we pre-search linked-account candidates on the related launcher.
// game/launcher are lower-cased substrings matched against names.
const CROSS_LAUNCHER_HINTS = [
  { game: 'grand theft auto v', launcher: 'rockstar' },
]

// How many arenas to show before the user starts typing to search.
const ARENA_PREVIEW_COUNT = 10

// Games are fetched a page at a time (browse + search), loading more on scroll.
const GAMES_PAGE_SIZE = 20

const emptyForm = () => ({
  app_id: null,
  username: '',
  password: '',
  secret: '',
  email: '',
  game_ids: [],
  arena_ids: [],
  linked_account_id: null,
})

export default {
  name: 'AddAccountDialog',
  mixins: [required],
  props: {
    value: { type: Boolean, default: false },
    arenaId: { type: [Number, String], default: null },
    // [{ text, value }] launchers (apps tagged "launcher") from the parent.
    launchers: { type: Array, default: () => [] },
    // When set, the dialog edits this SharedAccountListItem instead of creating.
    account: { type: Object, default: null },
  },
  data() {
    return {
      form: emptyForm(),
      submitting: false,
      serverError: '',
      showPassword: false,
      showSecret: false,
      // games (paginated browse + async search) - keep an id->name map so chips
      // keep their names. gamesPage/gamesHasMore drive infinite scroll.
      gameSearch: '',
      gameDebounce: null,
      gamesLoading: false,
      gamesLoadingMore: false,
      gamesPage: 0,
      gamesHasMore: true,
      gameNamesById: {},
      // arenas (loaded once, filtered client-side)
      arenaItems: [],
      arenaSearch: '',
      arenasLoading: false,
      // linked account (async search)
      linkedSearch: '',
      linkedDebounce: null,
      linkedLoading: false,
      linkedNamesById: {},
      // launcher ids we've already auto pre-searched, so we do it once each
      linkedPresearchedApps: [],
    }
  },
  computed: {
    isEdit() {
      return !!this.account
    },
    isOpen: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },
    // Password is required on create; on edit a blank field means "unchanged".
    passwordRules() {
      return this.isEdit ? [] : [this.required]
    },
    gameItems() {
      return Object.entries(this.gameNamesById)
        .map(([value, text]) => ({ value, text }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },
    // Guide the empty state: <2 chars can't trigger a server search.
    gamesNoData() {
      return (this.gameSearch || '').trim().length >= 2
        ? this.$t('components.noData.noEntriesFound')
        : this.$t('sharedAccounts.add.gamesTypeMore')
    },
    // Show a short preview of arenas until the user types, then filter the full
    // (already-loaded) list client-side. Selected arenas are always included so
    // their chips keep resolving to names regardless of the search term.
    visibleArenaItems() {
      const term = (this.arenaSearch || '').trim().toLowerCase()
      const selected = new Set(this.form.arena_ids)
      const list = term
        ? this.arenaItems.filter((a) => a.text.toLowerCase().includes(term))
        : this.arenaItems.slice(0, ARENA_PREVIEW_COUNT)
      const byValue = new Map(list.map((a) => [a.value, a]))
      // Ensure every selected arena is present even if outside the preview/filter.
      for (const a of this.arenaItems) {
        if (selected.has(a.value)) byValue.set(a.value, a)
      }
      return [...byValue.values()]
    },
    // Tell the admin the menu is previewing a subset and is searchable.
    arenasHint() {
      if (this.arenaSearch) return ''
      if (this.arenaItems.length <= ARENA_PREVIEW_COUNT) return ''
      return this.$t('sharedAccounts.add.arenasSearchHint', {
        count: ARENA_PREVIEW_COUNT,
      })
    },
    linkedItems() {
      return Object.entries(this.linkedNamesById).map(([value, text]) => ({
        value,
        text,
      }))
    },
    gamesRule() {
      return () =>
        this.form.game_ids.length > 0 ||
        this.$t('sharedAccounts.add.gamesRequired')
    },
    arenasRule() {
      return () =>
        this.form.arena_ids.length > 0 || this.$t('validation.required')
    },
  },
  watch: {
    value(open) {
      if (open) this.init()
    },
    gameSearch() {
      clearTimeout(this.gameDebounce)
      this.gameDebounce = setTimeout(() => this.fetchGamesPage(true), 400)
    },
    linkedSearch(val) {
      clearTimeout(this.linkedDebounce)
      this.linkedDebounce = setTimeout(() => this.searchLinked(val), 400)
    },
    'form.game_ids'(ids) {
      // clear the leftover typed term so the field resets after each pick
      this.gameSearch = ''
      this.maybePresearchLinked(ids)
    },
    'form.arena_ids'() {
      // reset the preview/search after each pick so the next open shows the list
      this.arenaSearch = ''
    },
  },
  methods: {
    ...mapActions(['showSnackbar']),
    init() {
      this.form = emptyForm()
      this.serverError = ''
      this.showPassword = false
      this.showSecret = false
      this.gameNamesById = {}
      this.linkedNamesById = {}
      this.linkedPresearchedApps = []
      this.gameSearch = ''
      this.linkedSearch = ''
      this.arenaSearch = ''
      if (this.isEdit) {
        this.prefillFromAccount()
      } else {
        this.form.arena_ids = this.arenaId ? [this.arenaId] : []
      }
      if (this.$refs.form) this.$refs.form.resetValidation()
      this.fetchArenas()
      this.fetchGamesPage(true)
    },
    // Fetch one page of games. reset=true starts a fresh query (browse when the
    // search box is empty, name-filtered when >=2 chars); otherwise it appends
    // the next page for infinite scroll. Results merge into gameNamesById so
    // selected chips keep their names. hasMore is inferred from the batch size.
    async fetchGamesPage(reset = false) {
      const term = (this.gameSearch || '').trim()
      // A single character is too short for a useful server search; wait.
      if (term.length === 1) return
      if (reset) {
        this.gamesPage = 0
        this.gamesHasMore = true
      } else if (
        !this.gamesHasMore ||
        this.gamesLoading ||
        this.gamesLoadingMore
      ) {
        return
      }
      const nextPage = this.gamesPage + 1
      if (reset) this.gamesLoading = true
      else this.gamesLoadingMore = true
      try {
        const nameParam =
          term.length >= 2 ? `&name=${encodeURIComponent(term)}` : ''
        const response = await GamesApi.get(
          `page=${nextPage}&size=${GAMES_PAGE_SIZE}&sort=name${nameParam}`,
        )
        const games = response.data?.data || []
        const map = { ...this.gameNamesById }
        for (const g of games) {
          if (g && g.id) map[g.id] = g.name
        }
        this.gameNamesById = map
        this.gamesPage = nextPage
        this.gamesHasMore = games.length === GAMES_PAGE_SIZE
      } catch (e) {
        // leave existing items
      } finally {
        this.gamesLoading = false
        this.gamesLoadingMore = false
      }
    },
    onGamesScrollEnd(entries, observer, isIntersecting) {
      if (isIntersecting) this.fetchGamesPage(false)
    },
    // Seed the form from the row's SharedAccountListItem. password/secret are
    // never returned, so they stay blank (blank => unchanged on PATCH).
    prefillFromAccount() {
      const a = this.account
      this.form.app_id = (a.app && a.app.id) || a.app_id || null
      this.form.username = a.username || ''
      this.form.email = a.email || ''
      this.form.arena_ids = Array.isArray(a.arena_ids) ? [...a.arena_ids] : []
      const games = Array.isArray(a.games) ? a.games : []
      this.form.game_ids = games.map((g) => g.game_id)
      const gameMap = {}
      for (const g of games) {
        if (g && g.game_id) gameMap[g.game_id] = g.name
      }
      this.gameNamesById = gameMap
      this.form.linked_account_id = a.linked_account_id || null
      // The list item carries only the linked id, not its name; show a stub
      // label until the user re-searches and resolves the real account.
      if (a.linked_account_id) {
        this.linkedNamesById = {
          [a.linked_account_id]: this.$t('sharedAccounts.edit.linkedExisting'),
        }
      }
    },
    // When a multi-launcher game (e.g. GTA V) is among the selected games,
    // pre-load linked-account candidates from the related launcher so the user
    // doesn't have to know to search for them.
    maybePresearchLinked(ids) {
      for (const id of ids || []) {
        const name = (this.gameNamesById[id] || '').toLowerCase()
        const hint = CROSS_LAUNCHER_HINTS.find((h) => name.includes(h.game))
        if (!hint) continue
        const launcher = this.launchers.find((l) =>
          (l.text || '').toLowerCase().includes(hint.launcher),
        )
        if (!launcher || !launcher.value) continue
        if (this.linkedPresearchedApps.includes(launcher.value)) continue
        this.linkedPresearchedApps.push(launcher.value)
        this.searchLinkedByApp(launcher.value)
      }
    },
    async searchLinkedByApp(appId) {
      this.linkedLoading = true
      try {
        const response = await SharedAccount.api.list(
          this.arenaId,
          `app_id=${encodeURIComponent(appId)}&size=50`,
        )
        const accounts = response.data?.data || []
        const map = { ...this.linkedNamesById }
        for (const a of accounts) {
          if (!a || !a.id) continue
          // An account cannot be linked to itself.
          if (this.account && a.id === this.account.id) continue
          const launcher = a.app && a.app.name ? ` · ${a.app.name}` : ''
          map[a.id] = `${a.username}${launcher}`
        }
        this.linkedNamesById = map
      } catch (e) {
        // leave existing items
      } finally {
        this.linkedLoading = false
      }
    },
    async fetchArenas() {
      this.arenasLoading = true
      try {
        // arena-hub list endpoints cap size at 100; the rest of the app uses
        // size=100 for arena pickers, so we match it. Sorting is done
        // client-side below to avoid relying on an unverified sort param.
        const response = await ArenasApi.get('size=100')
        const arenas = response.data?.data || response.data || []
        this.arenaItems = (Array.isArray(arenas) ? arenas : [])
          .map((a) => ({
            value: a.id,
            text: a.name || `#${a.id}`,
          }))
          .sort((a, b) => a.text.localeCompare(b.text))
      } catch (e) {
        // Fall back to just the current arena id so the form still works.
        this.arenaItems = this.arenaId
          ? [{ value: this.arenaId, text: `#${this.arenaId}` }]
          : []
      } finally {
        this.arenasLoading = false
      }
    },
    async searchLinked(term) {
      const q = (term || '').trim()
      if (q.length < 2) return
      this.linkedLoading = true
      try {
        const response = await SharedAccount.api.list(
          this.arenaId,
          `username=${encodeURIComponent(q)}&size=50`,
        )
        const accounts = response.data?.data || []
        const map = { ...this.linkedNamesById }
        for (const a of accounts) {
          if (!a || !a.id) continue
          // An account cannot be linked to itself.
          if (this.account && a.id === this.account.id) continue
          const launcher = a.app && a.app.name ? ` · ${a.app.name}` : ''
          map[a.id] = `${a.username}${launcher}`
        }
        this.linkedNamesById = map
      } catch (e) {
        // leave existing items
      } finally {
        this.linkedLoading = false
      }
    },
    async submit() {
      if (!this.$refs.form.validate()) return
      this.submitting = true
      this.serverError = ''
      try {
        if (this.isEdit) {
          await this.submitUpdate()
        } else {
          await this.submitCreate()
        }
      } catch (e) {
        // Do NOT log: the request body carries password/secret.
        this.serverError = this.errorMessage(e)
      } finally {
        this.submitting = false
      }
    },
    async submitCreate() {
      const payload = {
        app_id: this.form.app_id,
        username: this.form.username.trim(),
        password: this.form.password,
        secret: this.form.secret ? this.form.secret : null,
        email: this.form.email ? this.form.email.trim() : null,
        game_ids: this.form.game_ids,
        arena_ids: this.form.arena_ids.length
          ? this.form.arena_ids
          : [this.arenaId],
        linked_account_id: this.form.linked_account_id || null,
      }
      await SharedAccountsApi.create(this.arenaId, payload)
      this.showSnackbar({
        color: 'success',
        message: this.$t('sharedAccounts.add.success'),
      })
      this.$emit('created')
      this.close()
    },
    async submitUpdate() {
      // PATCH: app_id & owner_id are immutable, so they're never sent. Blank
      // password/secret means "leave unchanged" - only send them when filled.
      const payload = {
        username: this.form.username.trim(),
        email: this.form.email ? this.form.email.trim() : null,
        game_ids: this.form.game_ids,
        arena_ids: this.form.arena_ids,
        linked_account_id: this.form.linked_account_id || null,
      }
      if (this.form.password) payload.password = this.form.password
      if (this.form.secret) payload.secret = this.form.secret
      await SharedAccountsApi.update(this.arenaId, this.account.id, payload)
      this.showSnackbar({
        color: 'success',
        message: this.$t('sharedAccounts.edit.success'),
      })
      this.$emit('updated')
      this.close()
    },
    errorMessage(e) {
      const status = e && e.response && e.response.status
      const data = (e && e.response && e.response.data) || {}
      const failed = this.isEdit
        ? this.$t('sharedAccounts.edit.failed')
        : this.$t('sharedAccounts.add.failed')
      if (status === 409) {
        return this.$t('sharedAccounts.add.duplicate')
      }
      if (status === 400) {
        return this.resolveServerMessage(data, failed)
      }
      return failed
    },
    // Backend error keys (e.g. "arena_hub.ERR030.message") are not in our
    // frontend catalog, so $t() would echo the raw key. Use the translation
    // only when we actually have it (current locale or the English fallback);
    // otherwise show the backend's plain message (English), then a generic one.
    resolveServerMessage(data, fallback) {
      const key = data && data.l10n_message_key
      if (key && (this.$te(key) || this.$te(key, 'en'))) {
        return this.$t(key)
      }
      return (data && data.message) || fallback
    },
    close() {
      this.isOpen = false
    },
  },
}
</script>
